import { ChevronRight, Pencil, Ruler, Trash2 } from "lucide-react";
import Flag from "react-world-flags";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import { formatRail } from "@/shared/utils/railDisplay";

import { RAIL_COUNTRY } from "../../constants/paymentRails.constants";
import type { AmountLimitCell } from "../../types/paymentLimits.types";
import { formatSince } from "../rails/railCellDisplay";
import { effectiveFor, formatBound, hasOwnOverride, rowsInScope, sourceKeyFor } from "./limitCellDisplay";

export interface AmountLimitsCardsProps {
  cells: AmountLimitCell[];
  isLoading: boolean;
  scopeOrgId: string | null;
  orgNameById: Map<string, string>;
  isMutating: boolean;
  onEdit: (cell: AmountLimitCell) => void;
  onRemove: (cell: AmountLimitCell) => void;
  onViewOrg: (organizationId: string) => void;
}

/**
 * Mobile rendering of one direction's corridors: one card per corridor with a bounds line per payer kind,
 * never a squeezed eight-column table. The source, reason and override list are inline — on touch there is no
 * hover to hide behind.
 */
export function AmountLimitsCards({
  cells,
  isLoading,
  scopeOrgId,
  orgNameById,
  isMutating,
  onEdit,
  onRemove,
  onViewOrg,
}: AmountLimitsCardsProps) {
  const { t, activeLanguage } = useTranslations("paymentLimits");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="h-36 animate-pulse rounded-xl border border-border bg-muted" />
        ))}
      </div>
    );
  }

  if (cells.length === 0) {
    return <ColocaEmpty icon={Ruler} title={t("empty_title")} description={t("empty_description")} />;
  }

  const boundsLine = (cell: AmountLimitCell, payerType: LimitPayerType): string => {
    const limit = effectiveFor(cell, payerType);
    if (!limit || (limit.min_amount === null && limit.max_amount === null)) return t("bound_none");
    return `${t("bound_min")} ${formatBound(limit.min_amount, cell.currency, activeLanguage, "—")} · ${t(
      "bound_max",
    )} ${formatBound(limit.max_amount, cell.currency, activeLanguage, "—")}`;
  };

  return (
    <div className="flex flex-col gap-3">
      {cells.map((cell) => {
        const country = RAIL_COUNTRY[cell.rail];
        const rows = rowsInScope(cell, scopeOrgId);
        const latest = rows
          .map((row) => row.since)
          .sort()
          .at(-1);
        const reason = rows.find((row) => row.reason)?.reason;
        const removable = scopeOrgId ? hasOwnOverride(cell, scopeOrgId) : cell.global.length > 0;
        return (
          <div
            key={`${cell.rail}:${cell.direction}:${cell.currency}`}
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              <ColocaBadge variant="secondary" className="font-sans">
                {formatRail(cell.rail)}
              </ColocaBadge>
              {country && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Flag code={country.iso2} className="h-3.5 w-5 shrink-0 rounded object-cover" />
                  {t(country.labelKey)}
                </span>
              )}
              <span className="ml-auto font-mono text-xs text-muted-foreground">{cell.currency}</span>
            </div>

            <dl className="space-y-1 font-mono text-xs tabular-nums">
              <div className="flex justify-between gap-3">
                <dt className="font-sans text-muted-foreground">{t("payer_person")}</dt>
                <dd>{boundsLine(cell, "PERSON")}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="font-sans text-muted-foreground">{t("payer_company")}</dt>
                <dd>{boundsLine(cell, "COMPANY")}</dd>
              </div>
            </dl>

            <p className="mt-2 text-xs text-muted-foreground">
              {t(sourceKeyFor(cell, scopeOrgId))}
              {latest ? ` · ${formatSince(latest, activeLanguage)}` : ""}
            </p>
            {reason && <p className="mt-1 text-xs text-muted-foreground">{reason}</p>}

            {!scopeOrgId && cell.org_overrides.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                <p className="text-xs text-muted-foreground">
                  {t("overrides_count", { count: cell.org_overrides.length })}
                </p>
                {cell.org_overrides.map((override) => (
                  <ColocaButton
                    key={override.organization_id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between gap-2"
                    onClick={() => onViewOrg(override.organization_id)}
                  >
                    <span className="truncate">
                      {orgNameById.get(override.organization_id) ?? override.organization_id}
                    </span>
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                  </ColocaButton>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center justify-end gap-1">
              {removable && (
                <ColocaButton
                  variant="ghost"
                  size="xs"
                  icon={Trash2}
                  disabled={isMutating}
                  onClick={() => onRemove(cell)}
                >
                  {t(scopeOrgId ? "action_clear_override" : "action_remove")}
                </ColocaButton>
              )}
              <ColocaButton
                variant="secondary"
                size="xs"
                icon={Pencil}
                disabled={isMutating}
                onClick={() => onEdit(cell)}
              >
                {t("action_edit")}
              </ColocaButton>
            </div>
          </div>
        );
      })}
    </div>
  );
}
